import { createSchema } from "@opstel/query/zod-utils";
import {
  PlanTrainUnitDetails as ProtoPlanTrainUnitDetails,
  PlanTrainUnitDetails_TransitionType,
  PlanTrainUnitDetails_MatchingChangeability,
} from "@opstel/gen/knop/ui/v1/api/plan_train_unit_details_pb";
import { PlainMessage, Duration as ProtoDuration, Timestamp } from "@bufbuild/protobuf";
import { ElementOf } from "@shared/common/types/typeHelpers";
import { DateTime, Duration } from "luxon";
import { z } from "zod/v3";
import { ServiceTask, serviceTaskSchema } from "./serviceTask";

export interface PlanTrainUnitDetails {
  ptuGuid: string;
  trainUnitKind: string;
  transition: TransitionType;
  transitionDuration: Duration;
  serviceTasks: ServiceTask[];
  claims: string[];
  appearingTrainUnitName: string;
  appearingTrackName: string;
  appearingTime: DateTime;
  disappearingTrainUnitName: string;
  disappearingTrackName: string;
  disappearingTime: DateTime;
  matchingChangeability: MatchingChangeability;
}

export const TRANSITION_TYPE = ["DAY", "NIGHT", "NIGHT_OUTSIDE_SCENARIO"] as const;
export type TransitionType = ElementOf<typeof TRANSITION_TYPE>;
type ValidTransitionTypes = Exclude<
  PlanTrainUnitDetails_TransitionType,
  PlanTrainUnitDetails_TransitionType.UNKNOWN
>;
function transformTransitionTypeEnum(transitionType: ValidTransitionTypes): TransitionType {
  const mapping: Record<ValidTransitionTypes, TransitionType> = {
    [PlanTrainUnitDetails_TransitionType.DAY]: "DAY",
    [PlanTrainUnitDetails_TransitionType.NIGHT]: "NIGHT",
    [PlanTrainUnitDetails_TransitionType.NIGHT_OUTSIDE_SCENARIO]: "NIGHT_OUTSIDE_SCENARIO",
  };
  return mapping[transitionType];
}

export const MATCHING_CHANGEABILITY = ["FIXED", "PINNED", "OPEN"] as const;
export type MatchingChangeability = ElementOf<typeof MATCHING_CHANGEABILITY>;
type ValidMatchingChangeability = Exclude<
  PlanTrainUnitDetails_MatchingChangeability,
  PlanTrainUnitDetails_MatchingChangeability.MATCHING_CHANGEABILITY_UNKNOWN
>;
export function sortMatchingChangeability(
  a: MatchingChangeability,
  b: MatchingChangeability,
): number {
  const aIndex = MATCHING_CHANGEABILITY.indexOf(a);
  const bIndex = MATCHING_CHANGEABILITY.indexOf(b);
  return aIndex - bIndex;
}

function transformMatchingChangeabilityEnum(
  ability: ValidMatchingChangeability,
): MatchingChangeability {
  const mapping: Record<ValidMatchingChangeability, MatchingChangeability> = {
    [PlanTrainUnitDetails_MatchingChangeability.FIXED]: "FIXED",
    [PlanTrainUnitDetails_MatchingChangeability.PINNED]: "PINNED",
    [PlanTrainUnitDetails_MatchingChangeability.OPEN]: "OPEN",
  };
  return mapping[ability];
}

export const PlanTrainUnitDetailsSchema = createSchema<
  PlanTrainUnitDetails,
  PlainMessage<ProtoPlanTrainUnitDetails>
>({
  ptuGuid: z.string().uuid({ message: "PlanTrainUnitDetails ptuGuid should be a valid uuid" }),
  trainUnitKind: z.string(),
  transition: z
    .nativeEnum(PlanTrainUnitDetails_TransitionType)
    .refine((val) => val !== PlanTrainUnitDetails_TransitionType.UNKNOWN, {
      message: "PlanTrainUnitDetails transition should not be UNKNOWN",
    })
    .transform(transformTransitionTypeEnum),
  transitionDuration: z
    .instanceof(ProtoDuration)
    .transform((val) => Duration.fromObject({ seconds: Number(val.seconds) }))
    .refine((val) => val.as("seconds") >= 0, {
      message: "PlanTrainUnitDetails transitionDuration should be non-negative",
    }),
  serviceTasks: z.array(serviceTaskSchema),
  claims: z.array(z.string()),
  appearingTrainUnitName: z.string(),
  appearingTrackName: z.string(),
  appearingTime: z
    .instanceof(Timestamp, { message: "PlanTrainUnitDetails appearingTime should not be empty" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  disappearingTrainUnitName: z.string(),
  disappearingTime: z
    .instanceof(Timestamp, { message: "PlanTrainUnitDetails disappearingTime should not be empty" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  disappearingTrackName: z.string(),
  matchingChangeability: z
    .nativeEnum(PlanTrainUnitDetails_MatchingChangeability)
    .refine(
      (val) => val !== PlanTrainUnitDetails_MatchingChangeability.MATCHING_CHANGEABILITY_UNKNOWN,
      {
        message: "matchingChangeability should not be UNKNOWN",
      },
    )
    .transform((val) => transformMatchingChangeabilityEnum(val)),
});

export const transitionTypeTranslation: Record<TransitionType, string> = {
  DAY: "Dagovergang",
  NIGHT: "Nachtovergang",
  NIGHT_OUTSIDE_SCENARIO: "Nachtovergang buiten scenario",
};
